import { useCallback, useEffect, useState } from "react";
import {
  playerAIAppIdForScope,
  playerAIReadinessFromError,
  playerAIReadinessFromProfile,
  useOptionalPlayerAIClient,
  type PlayerAIReadiness,
  type PlayerAIScope,
} from "@playbookai/player-ai-client";

/**
 * Module visibility d6: can Player AI answer here at all? Read once when the
 * panel opens — and again when it moves to another app — from the app profile
 * read, so "Player AI isn't set up yet" shows before anyone types.
 *
 * `null` while the read is in flight, and when no client is mounted: the
 * panel then says nothing rather than guessing. `retry` re-reads, for the
 * temporary-outage card's button.
 */
export function usePlayerAIReadiness(scope: PlayerAIScope | null): {
  readonly readiness: PlayerAIReadiness | null;
  readonly retry: () => void;
} {
  const client = useOptionalPlayerAIClient();
  const appId = playerAIAppIdForScope(scope);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    readonly key: string;
    readonly readiness: PlayerAIReadiness;
  } | null>(null);
  const key = `${appId}:${attempt}`;

  useEffect(() => {
    if (!client) return undefined;
    let current = true;
    client.getAppProfile(appId).then(
      (configuration) => {
        if (current) {
          setResult({
            key,
            readiness: playerAIReadinessFromProfile(configuration),
          });
        }
      },
      (error: unknown) => {
        if (current) {
          setResult({ key, readiness: playerAIReadinessFromError(error) });
        }
      },
    );
    return () => {
      current = false;
    };
  }, [appId, client, key]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return {
    // An answer for another app, or for an earlier attempt, is not this one.
    readiness: result?.key === key ? result.readiness : null,
    retry,
  };
}
